import { useCallback, useRef } from 'react';

/** Matches the PDP media carousel's settle window, so a swipe reports once, not per frame. */
const SCROLL_SETTLE_MS = 150;

/** Below this, a scroll is a stray pixel or a bounce rather than a move between cards. */
const MIN_SCROLL_PX = 8;

/**
 * Reports which way the customer swiped the preview, once per settled movement. The row
 * has no arrows, so the scroll container is the only place a move can be observed. One
 * swipe emits scroll events for as long as the momentum lasts, hence the settle debounce.
 *
 * Returns a callback ref, not a ref object: the row mounts after the skeleton, once the
 * first page lands, so an effect run on mount would find no node and never listen.
 */
export const usePreviewScrollTracking = (onScroll?: (direction: 'next' | 'previous') => void) => {
  // A ref, so a new closure each render does not re-bind the listener and drop a settle timer.
  const onScrollRef = useRef(onScroll);
  const detachRef = useRef<() => void>();

  onScrollRef.current = onScroll;

  return useCallback((track: HTMLUListElement | null) => {
    detachRef.current?.();
    detachRef.current = undefined;

    if (!track) {
      return;
    }

    let settleTimeoutId: ReturnType<typeof setTimeout>;
    let lastReportedLeft = track.scrollLeft;

    const onScrollSettled = () => {
      const traveled = track.scrollLeft - lastReportedLeft;

      if (Math.abs(traveled) < MIN_SCROLL_PX) {
        return;
      }

      lastReportedLeft = track.scrollLeft;
      onScrollRef.current?.(traveled > 0 ? 'next' : 'previous');
    };

    const handleScroll = () => {
      clearTimeout(settleTimeoutId);
      settleTimeoutId = setTimeout(onScrollSettled, SCROLL_SETTLE_MS);
    };

    track.addEventListener('scroll', handleScroll, { passive: true });

    detachRef.current = () => {
      clearTimeout(settleTimeoutId);
      track.removeEventListener('scroll', handleScroll);
    };
  }, []);
};
